import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useNative } from "../../../shared/phone/media";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { Sheet } from "../../components/ui/Sheet";

export interface WalkPart {
  id: string;
  title: string;
  /** Where this part comes from, behind an info note on its heading. */
  why?: string;
}

interface Props {
  parts: WalkPart[];
  /** The rail's head: the title (shown when the walk sits inside a page), version and origin chips, one line. */
  head: { title: string; ver: string; badge: string; about?: string; sub?: string; small?: string };
  inline?: boolean;
  edited?: (part: WalkPart) => boolean;
  marker?: (part: WalkPart) => ReactNode;
  body: (part: WalkPart) => ReactNode;
  foot?: ReactNode;
  /** Back and Next between parts. Off inside the wizard, whose own Back and Next move between steps. */
  nav?: boolean;
  done?: { label: string; onClick: () => void };
}

/** The prototype's doc walk (`.walk.dw`): a rail of parts and one part at a time, for a playbook or a plan. */
/** The rail's one line about the document: three lines at most, the rest behind Show more, so the parts stay in view. */
function Line({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setLong(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  return (
    <>
      <p className={`dw-line${open ? "" : " clamp"}`} ref={ref}>
        {text}
      </p>
      {(long || open) && (
        <button type="button" className="dw-more" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}

export function DocWalk({ parts, head, inline = false, edited, marker, body, foot, nav = true, done }: Props) {
  // Why: on a phone and an iPad the list of parts is the kit's sheet, its rows big enough for a finger.
  const native = useNative();
  const [at, setAt] = useState(0);
  const [map, setMap] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const i = Math.min(at, parts.length - 1);
  const part = parts[i];

  const go = (k: number) => {
    setAt(k);
    setMap(false);
    const w = ref.current;
    if (w && w.getBoundingClientRect().top < 0) w.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  };

  const list = (
    <ol className="wr-list dw-list">
      {parts.map((p, k) => {
        const ed = edited?.(p) ?? false;
        return (
          <li key={p.id}>
            <button type="button" className={`${k === i ? "on" : ""} ${ed ? "edited" : ""}`} onClick={() => go(k)}>
              <span>{p.title}</span>
              {ed && (
                <em className="e">
                  <Icon name="pen" />
                  Your edits
                </em>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );

  if (!part) return null;
  const next = parts[i + 1];
  return (
    <div className="walk dw" ref={ref}>
      <aside className="wrail">
        <div className="dw-h">
          {inline && <div className="dw-t">{head.title}</div>}
          <div className="dw-meta">
            <span className="chip">{head.ver}</span>
            <span className="chip">{head.badge}</span>
            {head.about && <Info label="About it">{head.about}</Info>}
          </div>
          {inline && head.sub && <Line text={head.sub} />}
          {head.small && <p className="dw-date">{head.small}</p>}
        </div>
        {list}
        {foot && <div className="dw-foot">{foot}</div>}
        <button type="button" className="chip link wr-mob" onClick={() => setMap(true)}>
          Part {i + 1} of {parts.length} · All parts
          <Icon name="chev" />
        </button>
      </aside>
      <section className="card wcard">
        <div className="wk-top">
          <h2 className="wk-h h-i">
            {part.title}
            {part.why && <Info label="Where this comes from">{part.why}</Info>}
          </h2>
          <span className="dw-mk">{marker?.(part)}</span>
        </div>
        {body(part)}
        {nav && (
          <div className="wk-nav">
            {i > 0 && (
              <button type="button" className="btn ghost" onClick={() => go(i - 1)}>
                <Icon name="left" />
                Back
              </button>
            )}
            <span className="sp" />
            {next ? (
              <button type="button" className="btn" onClick={() => go(i + 1)}>
                Next: {next.title}
                <Icon name="right" />
              </button>
            ) : (
              done && (
                <button type="button" className="btn" onClick={done.onClick}>
                  {done.label}
                  <Icon name="check" />
                </button>
              )
            )}
          </div>
        )}
      </section>
      {native ? (
        <PnSheet open={map} onClose={() => setMap(false)} title="All parts" tall>
          <div className="pn-grp">
            {parts.map((p, k) => (
              <button type="button" key={p.id} className={`pn-row${k === i ? " on" : ""}`} aria-current={k === i ? "step" : undefined} onClick={() => go(k)}>
                <span className="gp-n">{k + 1}</span>
                <span className="m">
                  <span className="t">{p.title}</span>
                  {edited?.(p) && <span className="s">Your edits</span>}
                </span>
              </button>
            ))}
          </div>
        </PnSheet>
      ) : (
        <Sheet open={map} onClose={() => setMap(false)} label="All parts">
          <div className="eyebrow" style={{ paddingRight: 44 }}>
            {head.title}
          </div>
          <h2 style={{ marginTop: 6 }}>All parts</h2>
          <div style={{ marginTop: 12 }}>{list}</div>
        </Sheet>
      )}
    </div>
  );
}
